import { useCallback, useEffect, useState } from 'react';
import { pdApi } from '../api';
import '../screens.css';
import './Optimizer.css';
import { HelpPanel } from '../HelpPanel';
import { OPT_SCENARIO_BUILDER } from '../helpContent';
import { PROMPT, SelectField } from '@platform/ui-kit';

/**
 * Portfolio Optimizer → Strategy and Constraints.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/optimizer/scenario-builder.ejs`
 * and `routes/optimizer.js`. Three panes, as the source has them: constraints on the left,
 * the ranked result in the middle, the portfolio summary on the right.
 *
 * IT ANSWERS ONE QUESTION: given a fixed budget, which opportunities should we fund? The
 * server ranks candidates by a weighted score and takes them greedily until the budget is
 * spent, respecting locks and mandatory items first.
 *
 * FOUR SOURCE BEHAVIOURS ARE SURFACED HERE RATHER THAN HIDDEN, because a user reading the
 * numbers deserves to know:
 *
 *   1. THE SIXTH WEIGHT IS GONE (owner's instruction, 2026-09-30). The source collects an
 *      MVP Value Strength weight, validates it into the 100% total, and then DISCARDS it —
 *      `routes/optimizer.js:387` normalises over `w1..w5` and no column stores it. It could
 *      not change a ranking, a selection, or a saved scenario; all it did was force the
 *      other five to be set to something that did not add up to what was applied.
 *
 *      Its 15 points are redistributed proportionally, so THE OPTIMIZER'S BEHAVIOUR IS
 *      UNCHANGED: the server already divided by 85, making the old defaults effective at
 *      23.5/23.5/17.6/17.6/17.6. The new defaults of 23/23/18/18/18 are those same numbers,
 *      now shown honestly and stored honestly on the scenario.
 *   2. With no budget resolved, nothing is selected beyond locked and mandatory items.
 *      That is counter-intuitive enough to warrant a line on screen.
 *   3. Only Option B opportunities are candidates — the express lane skipped the analysis,
 *      so it is not optimised.
 *   4. Saving a scenario that contains a mandatory pick currently fails on a database
 *      constraint. Flagged in docs/DEVIATIONS.md; the button surfaces the real error.
 */

type Dict = Record<string, unknown>;

interface Weights {
  w1: number;
  w2: number;
  w3: number;
  w4: number;
  w5: number;
}

/*
 * 23/23/18/18/18 is the OLD BEHAVIOUR WRITTEN DOWN. The screen used to send
 * 20/20/15/15/15/15 and the server divided by 85, so the weights actually applied were
 * 23.5/23.5/17.6/17.6/17.6. These are those, rounded to integers that still total 100 and
 * keep the two-tier shape the source chose — ROI and criticality above the other three.
 */
const DEFAULT_WEIGHTS: Weights = { w1: 23, w2: 23, w3: 18, w4: 18, w5: 18 };

const WEIGHT_LABELS: { key: keyof Weights; label: string; note?: string }[] = [
  { key: 'w1', label: 'Return on Investment' },
  { key: 'w2', label: 'Business Criticality' },
  { key: 'w3', label: 'Strategic Alignment' },
  { key: 'w4', label: 'Benefit/Cost Effectiveness' },
  { key: 'w5', label: 'Delivery Risk (Subtracted)' },
];

export function ScenarioBuilder() {
  const [projects, setProjects] = useState<Dict[]>([]);
  const [projectId, setProjectId] = useState('');
  const [weights, setWeights] = useState<Weights>({ ...DEFAULT_WEIGHTS });
  const [budgetOverride, setBudgetOverride] = useState('');
  const [includeOnlyFeasible, setIncludeOnlyFeasible] = useState(false);
  const [excludeHighRisk, setExcludeHighRisk] = useState(false);
  const [highRiskThreshold, setHighRiskThreshold] = useState(4);
  const [respectMandatory, setRespectMandatory] = useState(true);
  const [locks, setLocks] = useState<Set<string>>(new Set());
  const [exclusions, setExclusions] = useState<Set<string>>(new Set());

  const [result, setResult] = useState<Dict | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState<{ name: string; description: string } | null>(null);

  const selectedProject = projects.find((p) => String(p.project_id) === projectId);

  /* The source closes the menu on any click outside it (`scenario-builder.ejs:1518`). */
  useEffect(() => {
    void (async () => {
      try {
        const data = await pdApi.optimizer.projects();
        setProjects(data.projects);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, []);

  const total = WEIGHT_LABELS.reduce((s, w) => s + weights[w.key], 0);
  const totalOk = total === 100;

  const touch = useCallback(() => setDirty(true), []);

  const run = async () => {
    if (!projectId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = (await pdApi.optimizer.optimize({
        project_id: projectId,
        weights,
        flags: { includeOnlyFeasible, excludeHighRisk, highRiskThreshold, respectMandatory },
        locks: [...locks],
        exclusions: [...exclusions],
        budgetOverride: budgetOverride || undefined,
      })) as Dict;
      setResult(r);
      setDirty(false);
      if (r.success === false) setError(String(r.error ?? 'Optimization failed'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const selection = (result?.selection as Dict | undefined)?.selected as Dict[] | undefined;
  const kpis = result?.kpis as Dict | undefined;
  const messages = (result?.messages as string[]) ?? [];
  const budget = result?.budget as number | null | undefined;

  const money = (v: unknown) =>
    v === null || v === undefined
      ? '—'
      : Number(v).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        });

  const toggleIn = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
    touch();
  };

  const save = async () => {
    if (!saving || !result) return;
    setBusy(true);
    setError(null);
    try {
      const r = await pdApi.optimizer.saveScenario({
        project_id: projectId,
        scenario_name: saving.name,
        scenario_description: saving.description,
        budget,
        weights,
        flags: { includeOnlyFeasible, excludeHighRisk, highRiskThreshold, respectMandatory },
        kpis,
        selected: selection ?? [],
      });
      setNotice(r.message);
      setSaving(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Portfolio Optimizer - What If Analysis</h1>
          <p className="pd-page-header__subtitle">
            Optimize opportunity selection using backward-planning with multi-criteria
            scoring
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={OPT_SCENARIO_BUILDER} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      <div className="opt-grid">
        {/* ---- Pane A: constraints ---- */}
        <div className="opt-pane">
          <h2 className="opt-pane-title">Constraints &amp; Strategy</h2>

          <div className="opt-section">
            <div className="opt-section-title">Project &amp; Budget</div>
            {/*
              THE PLATFORM'S PROJECT FIELD, not the source's hand-built dropdown.
              Owner ruling, 2026-09-30: *"there is a clear line between the projects - this
              is not how it should be designed. The design behind the project id pull down
              should be identical to the design that is made on screen All opportunities Card
              view"*.

              The port faithfully reproduced `scenario-builder.ejs:903-911` — a div, an arrow
              and a menu whose rows carry `border-bottom: 1px solid #f0f0f0`. That rule is the
              line he saw, and no other project picker in the product has one. `SelectField`
              is the single design for this control (380px, bold id then description), so the
              bespoke one is replaced rather than restyled: restyling it would have left two
              implementations of one control, which is how they drift apart.

              A deviation from the source, logged in docs/DEVIATIONS.md.
            */}
            <div className="opt-fg">
              <SelectField
                style={{ alignItems: 'center' }}
                label="Project"
                width="lg"
                id="opt-project"
                value={projectId}
                placeholder={PROMPT.project}
                options={projects.map((p) => ({
                  value: String(p.project_id),
                  id: String(p.project_id),
                  description: String(p.project_description ?? ''),
                }))}
                onChange={(v) => {
                  setProjectId(v);
                  setResult(null);
                  setLocks(new Set());
                  setExclusions(new Set());
                }}
              />
            </div>

            {/*
              The source leaves the menu blank when the API returns nothing, which reads as a
              broken control rather than an empty one. Saying WHY costs a line and is the
              difference between "this is broken" and "there is nothing to optimise yet". It
              sits beside the field now rather than inside the menu, because an empty
              SelectField has no menu to put it in.
            */}
            {projects.length === 0 ? (
              <div className="opt-dd-empty">
                No projects are ready to optimise. The Portfolio Optimizer only considers
                opportunities on <strong>Delivery Pathway B</strong> — an express-lane
                (Pathway A) opportunity skipped the analysis it ranks on. Assign some on the{' '}
                <strong>Delivery Pathway</strong> screen and they will appear here.
              </div>
            ) : null}

            {/* Project Budget — a read-only display the port had dropped entirely. :912-918 */}
            <div className="opt-fg">
              <label>Project Budget</label>
              <div className="opt-budget-display">
                {selectedProject
                  ? selectedProject.budget != null
                    ? `$${Number(selectedProject.budget).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : 'No budget set for this project'
                  : 'Select a project to view budget'}
              </div>
            </div>
            <div className="pd-field">
              <label htmlFor="opt-budget">Budget Override (Optional)</label>
              <input
                id="opt-budget"
                type="number"
                placeholder="$"
                value={budgetOverride}
                onChange={(e) => {
                  setBudgetOverride(e.target.value);
                  touch();
                }}
              />
              <span className="pd-hint">
                Override the project budget for this scenario. With no budget resolvable at
                all, only locked and mandatory items are selected.
              </span>
            </div>
          </div>

          <div className="opt-section">
            <div className="opt-section-title">
              Portfolio Weights (Total must equal 100%)
            </div>
            {WEIGHT_LABELS.map((w) => (
              <div key={w.key} className="opt-slider">
                <div className="opt-slider-head">
                  <span>{w.label}</span>
                  <span className="opt-slider-value">{weights[w.key]}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={weights[w.key]}
                  onChange={(e) => {
                    setWeights({ ...weights, [w.key]: Number(e.target.value) });
                    touch();
                  }}
                />
                {w.note ? <span className="opt-slider-note">{w.note}</span> : null}
              </div>
            ))}
            <div className={`opt-total${totalOk ? ' opt-total--ok' : ' opt-total--bad'}`}>
              Total: {total}%
            </div>
          </div>

          <div className="opt-section">
            <div className="opt-section-title">Constraints &amp; Filters</div>
            <label className="opt-check">
              <input
                type="checkbox"
                checked={includeOnlyFeasible}
                onChange={(e) => {
                  setIncludeOnlyFeasible(e.target.checked);
                  touch();
                }}
              />
              Include Only Feasible Opportunities (validate dates, capacity, dependencies)
            </label>
            <label className="opt-check">
              <input
                type="checkbox"
                checked={excludeHighRisk}
                onChange={(e) => {
                  setExcludeHighRisk(e.target.checked);
                  touch();
                }}
              />
              Exclude High-Risk Opportunities (risk score &gt;= threshold)
            </label>
            {excludeHighRisk ? (
              <div className="opt-slider">
                <div className="opt-slider-head">
                  <span>Risk threshold</span>
                  <span className="opt-slider-value">{highRiskThreshold}</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={highRiskThreshold}
                  onChange={(e) => {
                    setHighRiskThreshold(Number(e.target.value));
                    touch();
                  }}
                />
              </div>
            ) : null}
            <label className="opt-check">
              <input
                type="checkbox"
                checked={respectMandatory}
                onChange={(e) => {
                  setRespectMandatory(e.target.checked);
                  touch();
                }}
              />
              Respect Mandatory Opportunities (always include mandatory items)
            </label>
          </div>

          <button
            type="button"
            className="pd-btn pd-btn--primary opt-run"
            disabled={busy || !projectId || !totalOk}
            title={totalOk ? undefined : 'Weights must total 100%'}
            onClick={run}
          >
            {busy ? 'Running…' : '🚀 Run Optimization'}
          </button>
        </div>

        {/* ---- Pane B: ranked opportunities ---- */}
        <div className="opt-pane">
          <h2 className="opt-pane-title">
            Ranked Opportunities ({selection?.length ?? 0})
          </h2>

          {!result ? (
            <div className="pd-empty">
              <strong>No Results Yet</strong>
              <div>Run optimization to see ranked opportunities</div>
            </div>
          ) : (
            <div className="pd-table-wrap opt-rank">
              <table className="pd-table">
                <thead>
                  <tr>
                    <th style={{ width: 240 }}>Opportunity</th>
                    <th style={{ width: 110 }}>Estimated Cost</th>
                    <th style={{ width: 110 }}>Annual Benefit</th>
                    <th style={{ width: 52 }}>Lock</th>
                    <th style={{ width: 64 }}>Exclude</th>
                    <th
                      style={{ width: 110 }}
                      title="w₁·ROI + w₂·Criticality + w₃·Alignment + w₄·(B/C) − w₅·Risk"
                    >
                      Portfolio Score
                    </th>
                    <th style={{ width: 100 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(selection ?? []).map((o) => {
                    const id = String(o.opportunity_id);
                    return (
                      <tr key={id}>
                        <td title={`${id} — ${String(o.description ?? '')}`}>
                          <div className="opt-id">{id}</div>
                          <div className="opt-desc">{String(o.description ?? '')}</div>
                        </td>
                        <td className="pd-num">{money(o.estimated_cost)}</td>
                        <td className="pd-num">{money(o.business_benefit)}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={locks.has(id)}
                            onChange={() => toggleIn(locks, setLocks, id)}
                            aria-label={`Lock ${id}`}
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={exclusions.has(id)}
                            onChange={() => toggleIn(exclusions, setExclusions, id)}
                            aria-label={`Exclude ${id}`}
                          />
                        </td>
                        <td className="pd-num">
                          {o.portfolio_score === null || o.portfolio_score === undefined
                            ? '—'
                            : Number(o.portfolio_score).toFixed(3)}
                        </td>
                        <td>
                          <span className="pd-badge" style={{ color: '#475569' }}>
                            {String(o.selection_status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ---- Pane C: summary ---- */}
        <div className="opt-pane">
          <h2 className="opt-pane-title">Portfolio Summary</h2>

          {dirty && result ? (
            <div className="opt-dirty">
              Settings changed — run optimization to refresh results.
            </div>
          ) : null}

          {!result ? (
            <div className="pd-empty">Run optimization to see the portfolio summary.</div>
          ) : (
            <>
              {budget !== null && budget !== undefined ? (
                <div className="opt-budget">
                  <div className="opt-budget-bar">
                    <div
                      className="opt-budget-used"
                      style={{
                        width: `${Math.min(
                          100,
                          (Number(kpis?.totalCostSelected ?? 0) / Math.max(1, Number(budget))) * 100,
                        )}%`,
                      }}
                    />
                  </div>
                  <div className="opt-budget-label">
                    {money(kpis?.totalCostSelected)} of {money(budget)}
                  </div>
                </div>
              ) : (
                <div className="opt-dirty">
                  No budget could be resolved — only locked and mandatory items are
                  selected.
                </div>
              )}

              <dl className="opt-kpis">
                <div>
                  <dt>Total Cost</dt>
                  <dd>{money(kpis?.totalCostSelected)}</dd>
                </div>
                <div>
                  <dt>Total Annual Benefit</dt>
                  <dd>{money(kpis?.totalBenefitSelected)}</dd>
                </div>
                <div>
                  <dt>Portfolio ROI</dt>
                  <dd>
                    {kpis?.portfolioROI === null || kpis?.portfolioROI === undefined
                      ? '—'
                      : `${(Number(kpis.portfolioROI) * 100).toFixed(1)}%`}
                  </dd>
                </div>
                <div>
                  <dt>Payback (months)</dt>
                  <dd>{kpis?.paybackMonths === null ? '—' : String(kpis?.paybackMonths ?? '—')}</dd>
                </div>
                <div>
                  <dt># Selected</dt>
                  <dd>{String(kpis?.countSelected ?? 0)}</dd>
                </div>
                <div>
                  <dt>Budget Remaining</dt>
                  <dd>{money(kpis?.budgetRemaining)}</dd>
                </div>
              </dl>

              {messages.length ? (
                <div className="opt-messages">
                  {messages.map((m) => (
                    <div key={m}>{m}</div>
                  ))}
                </div>
              ) : null}

              <button
                type="button"
                className="pd-btn pd-btn--primary"
                disabled={busy}
                onClick={() => setSaving({ name: '', description: '' })}
              >
                Save Scenario
              </button>
            </>
          )}
        </div>
      </div>

      {saving ? (
        <div className="pd-modal-backdrop" onClick={() => setSaving(null)}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal__head">
              <h2 className="pd-modal__title">Save Scenario</h2>
              <button type="button" className="pd-icon-btn" onClick={() => setSaving(null)}>
                ✕
              </button>
            </div>
            <div className="pd-modal__body">
              <div className="pd-field">
                <label htmlFor="opt-name">Scenario Name *</label>
                <input
                  id="opt-name"
                  type="text"
                  value={saving.name}
                  onChange={(e) => setSaving({ ...saving, name: e.target.value })}
                />
              </div>
              <div className="pd-field">
                <label htmlFor="opt-sdesc">Description (Optional)</label>
                <textarea
                  id="opt-sdesc"
                  value={saving.description}
                  onChange={(e) => setSaving({ ...saving, description: e.target.value })}
                />
              </div>
            </div>
            <div className="pd-modal__foot">
              <button type="button" className="pd-btn" onClick={() => setSaving(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                disabled={!saving.name || busy}
                onClick={save}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
